import { z } from "zod";

import { LATEST_LAYOUT_SCHEMA_VERSION } from "@/shared/domain/layouts/schemas";
import { datasourceSchema } from "@/shared/state/visualization/schema";

/**
 * `version` of the second workspace config shape: datasources plus a layout
 * reference (or inline definition) and an optional playhead, instead of a
 * fully bound viz State (v1, whose `version` is optional).
 */
export const WORKSPACE_CONFIG_V2_VERSION = "visualization_workspace_v2";

/**
 * Epoch nanoseconds. The backend writes `t` as a decimal string because it
 * exceeds Number's safe range; this schema is the one place it becomes a
 * bigint (a bigint is admitted for callers that already hold one).
 */
const nanosecondsSchema = z
  .union([z.bigint(), z.string().regex(/^\d+$/)])
  .transform((value) => BigInt(value));

const layoutRefSchema = z
  .object({
    layoutId: z.string().min(1).optional(),
    layoutDefinition: z.unknown().optional(),
    // Only meaningful with `layoutDefinition`; a saved layout carries its
    // own `schema_version`. It still defaults on the `layoutId` branch, where
    // readers ignore it.
    schemaVersion: z.number().int().default(LATEST_LAYOUT_SCHEMA_VERSION),
  })
  .refine(
    (layout) =>
      (layout.layoutId !== undefined) !==
      (layout.layoutDefinition !== undefined),
    { message: "Exactly one of layoutId or layoutDefinition is required" },
  );

export const workspaceConfigV2Schema = z.object({
  version: z.literal(WORKSPACE_CONFIG_V2_VERSION),
  datasources: z.array(datasourceSchema),
  layout: layoutRefSchema,
  t: nanosecondsSchema.optional(),
});

export type WorkspaceConfigV2 = z.infer<typeof workspaceConfigV2Schema>;

export class InvalidWorkspaceConfigV2Error extends Error {
  constructor(message: string, options?: ErrorOptions) {
    super(message, options);
    this.name = "InvalidWorkspaceConfigV2Error";
  }
}

/**
 * Discriminates on `version` only; narrows to the tag, not to a validated
 * config (`parseWorkspaceConfigV2` does that). Run this before
 * `VizConfig.from_obj`: v1's `version` is optional, so v1 parsing cannot
 * reject a v2 config.
 */
export function isWorkspaceConfigV2(
  obj: unknown,
): obj is { version: typeof WORKSPACE_CONFIG_V2_VERSION } {
  return (
    typeof obj === "object" &&
    obj !== null &&
    (obj as { version?: unknown }).version === WORKSPACE_CONFIG_V2_VERSION
  );
}

/**
 * @throws InvalidWorkspaceConfigV2Error when `obj` is not a valid v2 config
 */
export function parseWorkspaceConfigV2(obj: unknown): WorkspaceConfigV2 {
  const result = workspaceConfigV2Schema.safeParse(obj);
  if (!result.success) {
    throw new InvalidWorkspaceConfigV2Error(
      `Invalid ${WORKSPACE_CONFIG_V2_VERSION} config: ${result.error.message}`,
      { cause: result.error },
    );
  }
  return result.data;
}
